import { without } from 'lodash';
import {
  labeled,
  guidRequired,
  arrayOptional,
  custom,
  enumRequired,
  timeStampEpochRequired,
  numberRequired,
  boolean
} from '../../util/joi';
import { YesNoOption, yesNoOptions } from '../global/YesNoOption.enum';

export const schema = (Joi: any, label: string) => {
  const availableYesNoOptions = without(
    yesNoOptions,
    YesNoOption.none.name,
    YesNoOption.yes_discharged.name,
    YesNoOption.not_important.name,
    YesNoOption.unsure.name
  );

  return Joi.object()
    .keys({
      planChangesToTheirFinancialCircumstances: enumRequired(
        Joi,
        availableYesNoOptions,
        labeled(
          label,
          'Do the applicant(s) plan or anticipate changes to their future financial circumstances that could ADVERSELY Impact their ability to repay the loan?'
        )
      )
    })
    .when('.planChangesToTheirFinancialCircumstances.name', {
      is: YesNoOption.yes.name,
      then: Joi.object().keys({
        applicants: arrayOptional(
          Joi,
          Joi.object().keys({
            idContact: guidRequired(Joi),
            isChanged: boolean(Joi)
          })
        )
          .has(
            Joi.object().keys({
              idContact: guidRequired(Joi),
              isChanged: boolean(Joi).invalid(false)
            })
          )
          .required()
          .messages({
            'array.hasKnown': '{{#label}} At least one applicant must be selected.',
            'array.hasUnknown': '{{#label}} At least one applicant must be selected.',
            'array.required': '{{#label}} At least one applicant must be selected.'
          })
          .label(
            labeled(
              label,
              'Specify to which applicant(s) the changes apply and answer the remaining questions for all applicants together.'
            )
          )
      })
    })
    .when('.planChangesToTheirFinancialCircumstances.name', {
      is: YesNoOption.yes.name,
      then: custom(Joi)
        .object()
        .keys({
          natureOfPlannedChange: custom(Joi)
            .object()
            .keys({
              extendedUnpaidLeave: Joi.boolean(),
              reducedIncome: Joi.boolean(),
              endOfContract: Joi.boolean(),
              leavingEmployment: Joi.boolean(),
              increasedDebtRepayments: Joi.boolean(),
              largeExpenditure: Joi.boolean(),
              medicalTreatment: Joi.boolean(),
              other: Joi.boolean()
            })
            .orValue(
              'extendedUnpaidLeave',
              'reducedIncome',
              'endOfContract',
              'leavingEmployment',
              'increasedDebtRepayments',
              'largeExpenditure',
              'medicalTreatment',
              'other'
            )
            .label(labeled(label, 'What is the nature of the planned or anticipated change?'))
            .when('.extendedUnpaidLeave', {
              is: true,
              then: Joi.object().keys({
                extendedUnpaidLeaveFromDate: timeStampEpochRequired(Joi).label(
                  labeled(label, 'Extended unpaid leave from date')
                ),
                extendedUnpaidLeaveToDate: timeStampEpochRequired(Joi).label(
                  labeled(label, 'Extended unpaid leave to date')
                ),
                extendedUnpaidLeaveFinancialImpact: numberRequired(Joi, 1).label(
                  labeled(label, 'Extended unpaid leave financial impact')
                )
              })
            })

            .when('.reducedIncome', {
              is: true,
              then: Joi.object().keys({
                reducedIncomeFromDate: timeStampEpochRequired(Joi).label(
                  labeled(label, 'Reduced Income from date')
                ),
                reducedIncomeToDate: timeStampEpochRequired(Joi).label(
                  labeled(label, 'Reduced Income to date')
                ),
                reducedIncomeFinancialImpact: numberRequired(Joi, 1).label(
                  labeled(label, 'Reduced Income financial impact')
                )
              })
            })
            .when('.endOfContract', {
              is: true,
              then: Joi.object().keys({
                endOfContractFromDate: timeStampEpochRequired(Joi).label(
                  labeled(label, 'End Of Contract from date')
                ),
                endOfContractToDate: timeStampEpochRequired(Joi).label(
                  labeled(label, 'End Of Contract to date')
                ),
                endOfContractFinancialImpact: numberRequired(Joi, 1).label(
                  labeled(label, 'End Of Contract financial impact')
                )
              })
            })
            .when('.leavingEmployment', {
              is: true,
              then: Joi.object().keys({
                leavingEmploymentFromDate: timeStampEpochRequired(Joi).label(
                  labeled(label, 'Leaving Employment from date')
                ),
                leavingEmploymentToDate: timeStampEpochRequired(Joi).label(
                  labeled(label, 'Leaving Employment to date')
                ),
                leavingEmploymentFinancialImpact: numberRequired(Joi, 1).label(
                  labeled(label, 'Leaving Employment financial impact')
                )
              })
            })
            .when('.increasedDebtRepayments', {
              is: true,
              then: Joi.object().keys({
                increasedDebtRepaymentsFromDate: timeStampEpochRequired(Joi).label(
                  labeled(label, 'Increased Debt Repayments from date')
                ),
                increasedDebtRepaymentsToDate: timeStampEpochRequired(Joi).label(
                  labeled(label, 'Increased Debt Repayments to date')
                ),
                increasedDebtRepaymentsFinancialImpact: numberRequired(Joi, 1).label(
                  labeled(label, 'Increased Debt Repayments financial impact')
                )
              })
            })
            .when('.largeExpenditure', {
              is: true,
              then: Joi.object().keys({
                largeExpenditureFromDate: timeStampEpochRequired(Joi).label(
                  labeled(label, 'Large Expenditure from date')
                ),
                largeExpenditureToDate: timeStampEpochRequired(Joi).label(
                  labeled(label, 'Large Expenditure to date')
                ),
                largeExpenditureFinancialImpact: numberRequired(Joi, 1).label(
                  labeled(label, 'Large Expenditure financial impact')
                )
              })
            })
            .when('.medicalTreatment', {
              is: true,
              then: Joi.object().keys({
                medicalTreatmentFromDate: timeStampEpochRequired(Joi).label(
                  labeled(label, 'Medical Treatment from date')
                ),
                medicalTreatmentToDate: timeStampEpochRequired(Joi).label(
                  labeled(label, 'Medical Treatment to date')
                ),
                medicalTreatmentFinancialImpact: numberRequired(Joi, 1).label(
                  labeled(label, 'Medical Treatment financial impact')
                )
              })
            })
            .when('.other', {
              is: true,
              then: Joi.object().keys({
                otherFromDate: timeStampEpochRequired(Joi).label(
                  labeled(label, 'Other from date')
                ),
                otherToDate: timeStampEpochRequired(Joi).label(labeled(label, 'Other to date')),
                otherFinancialImpact: numberRequired(Joi, 1).label(
                  labeled(label, 'Other financial impact')
                )
              })
            })
        })
    })
    .when('.planChangesToTheirFinancialCircumstances.name', {
      is: YesNoOption.yes.name,
      then: custom(Joi)
        .object()
        .keys({
          applicantsIntendToMeetLoanRepayments: custom(Joi)
            .object()
            .keys({
              additionalIncomeSource: boolean(Joi),
              coApplicantsIncome: boolean(Joi),
              other: boolean(Joi),
              reduceExpenditure: boolean(Joi),
              saleOfAssets: boolean(Joi),
              savingsAndSuperannuation: boolean(Joi),
              savingsAndSuperanuation: boolean(Joi)
            })
            .orValue(
              'additionalIncomeSource',
              'coApplicantsIncome',
              'other',
              'reduceExpenditure',
              'saleOfAssets',
              'savingsAndSuperannuation',
              'other'
            )
            .label(
              labeled(
                label,
                'How do the applicant(s) intend to meet their loan repayments following the planned or anticipated change(s)?'
              )
            )
            .when('.additionalIncomeSource', {
              is: true,
              then: Joi.object().keys({
                additionalIncomeSourceComments: Joi.string()
                  .required()
                  .label(labeled(label, 'Additional Income Source Comments'))
              })
            })

            .when('.coApplicantsIncome', {
              is: true,
              then: Joi.object().keys({
                coApplicantsIncomeComments: Joi.string()
                  .required()
                  .label(labeled(label, 'Co applicants income Comments'))
              })
            })
            .when('.other', {
              is: true,
              then: Joi.object().keys({
                otherComments: Joi.string().required().label(labeled(label, 'Other Comments'))
              })
            })
            .when('.reduceExpenditure', {
              is: true,
              then: Joi.object().keys({
                reduceExpenditureComments: Joi.string()
                  .required()
                  .label(labeled(label, 'Reduce expenditure Comments'))
              })
            })
            .when('.saleOfAssets', {
              is: true,
              then: Joi.object().keys({
                saleOfAssetsComments: Joi.string()
                  .required()
                  .label(labeled(label, 'Sale of assets Comments'))
              })
            })
            .when('.savingsAndSuperannuation', {
              is: true,
              then: Joi.object().keys({
                savingsAndSuperannuationComments: Joi.string()
                  .required()
                  .label(labeled(label, 'Savings and superannuation Comments'))
              })
            })
            .when('.other', {
              is: true,
              then: Joi.object().keys({
                otherComments: Joi.string().required().label(labeled(label, 'Other Comments'))
              })
            })
        })
    });
};

export default {
  schema
};
